import { useMemo, useState, useEffect } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents, useMap } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { SearchUnit } from './SearchResultCard';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { X, Map } from 'lucide-react';
import { RemoteImage } from '../../../core/components/RemoteImage';

// Fix for default Leaflet icon issues in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

interface MapViewProps {
    units: SearchUnit[];
    projects: any[];
}

export const MapView = ({ units, projects }: MapViewProps) => {
    const { t } = useTranslation();
    const [selectedGroup, setSelectedGroup] = useState<any | null>(null);

    // Group units by project to avoid overlapping markers
    // Since units have latitude/longitude inherited from projects now
    const groupedData = useMemo(() => {
        const groups: Record<string, { lat: number, lng: number, title: string, items: SearchUnit[], boundary?: any }> = {};

        units.forEach(unit => {
            const lat = (unit as any).latitude;
            const lng = (unit as any).longitude;
            const boundary = (unit as any).boundary;

            if (lat && lng) {
                const key = `${lat},${lng}`;
                if (!groups[key]) {
                    groups[key] = {
                        lat,
                        lng,
                        title: unit.location || 'Location',
                        items: [],
                        boundary
                    };
                }
                groups[key].items.push(unit);
            }
        });

        // Also add projects that have no units returned in the search but match the query
        projects.forEach(project => {
            if (project.latitude && project.longitude) {
                const key = `${project.latitude},${project.longitude}`;
                if (!groups[key]) {
                    groups[key] = {
                        lat: project.latitude,
                        lng: project.longitude,
                        title: project.title,
                        items: [],
                        boundary: project.boundary
                    };
                }
            }
        });

        return Object.values(groups);
    }, [units, projects]);

    const defaultCenter: [number, number] = [30.0444, 31.2357]; // Cairo default
    const mapCenter = groupedData.length > 0 ? [groupedData[0].lat, groupedData[0].lng] as [number, number] : defaultCenter;

    // Map Event Listener Helper to Close Bottom Sheet on map click/drag
    const MapEventsHelper = () => {
        useMapEvents({
            click() {
                setSelectedGroup(null);
            },
            dragstart() {
                setSelectedGroup(null);
            }
        });
        return null;
    };

    // Component to handle flying to location on results change
    const MapUpdater = ({ center }: { center: [number, number] }) => {
        const map = useMap();
        useEffect(() => {
            if (center && map) {
                // Fly to the center when it updates, adjusting zoom to a good viewing distance
                map.flyTo(center, 12, { animate: true, duration: 1.5 });
            }
        }, [center, map]);
        return null;
    };

    // Custom DivIcon for the map markers showing price or count
    const createCustomIcon = (group: any) => {
        const hasUnits = group.items.length > 0;
        let displayHTML = '';

        if (hasUnits) {
            // Find lowest price to show "From X"
            const prices = group.items.map((u: any) => parseFloat(u.price.toString().replace(/,/g, ''))).filter((p: number) => !isNaN(p));
            const minPrice = prices.length > 0 ? Math.min(...prices) : group.items[0].price;

            displayHTML = `
                <div class="bg-secondary text-white font-bold px-3 py-1.5 rounded-full shadow-lg border-2 border-white whitespace-nowrap text-xs transform -translate-x-1/2 -translate-y-full hover:bg-primary transition-colors hover:scale-105 origin-bottom">
                    ${t('common.from', 'From')} ${minPrice} ${t('common.egp', 'EGP')}
                    ${group.items.length > 1 ? `<span class="bg-white/20 px-1.5 py-0.5 rounded text-[10px] ml-1">${group.items.length} units</span>` : ''}
                </div>
            `;
        } else {
            displayHTML = `
                <div class="bg-primary text-white font-bold px-3 py-1.5 rounded-full shadow-lg border-2 border-white whitespace-nowrap text-xs transform -translate-x-1/2 -translate-y-full hover:bg-secondary transition-colors hover:scale-105 origin-bottom">
                    ${group.title}
                </div>
            `;
        }

        return L.divIcon({
            className: 'custom-map-marker',
            html: displayHTML,
            iconSize: [0, 0], // CSS handles size and translation
            iconAnchor: [0, 0]
        });
    };

    return (
        <div className="w-full h-full min-h-[500px] md:min-h-full rounded-2xl overflow-hidden shadow-sm border border-gray-200 relative z-0">
            <MapContainer center={mapCenter} zoom={11} className="w-full h-full" zoomControl={false} attributionControl={false}>
                <MapEventsHelper />
                <MapUpdater center={mapCenter} />
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {/* Render Polygons for Boundaries */}
                {/* Render Polygons for Boundaries */}
                {groupedData.map((group, idx) => {
                    if (group.boundary && Array.isArray(group.boundary) && group.boundary.length > 2) {
                        const positions = group.boundary.map((p: any) => [p.lat, p.lng] as [number, number]);
                        return (
                            <Polygon
                                key={`poly-${idx}`}
                                positions={positions}
                                pathOptions={{ color: '#0070F0', fillColor: '#0070F0', fillOpacity: 0.2, weight: 2 }}
                            />
                        );
                    }
                    return null;
                })}

                {/* Render Markers */}
                {groupedData.map((group, idx) => {
                    const isSelected = selectedGroup?.lat === group.lat && selectedGroup?.lng === group.lng;
                    return (
                        <Marker
                            key={`marker-${idx}`}
                            position={[group.lat, group.lng]}
                            icon={createCustomIcon(group)}
                            eventHandlers={{
                                click: () => {
                                    setSelectedGroup(group);
                                }
                            }}
                            zIndexOffset={isSelected ? 1000 : 0}
                        />
                    );
                })}
            </MapContainer>

            {/* Native Mobile Bottom Sheet / Desktop Overlay Card */}
            {selectedGroup && (
                <div className="absolute bottom-0 left-0 right-0 md:bottom-6 md:left-auto md:right-6 md:w-[400px] bg-white md:rounded-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.1)] md:shadow-2xl z-[1001] transition-transform animate-slide-up rounded-t-2xl border border-gray-100 max-h-[70vh] flex flex-col pointer-events-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-white/90 backdrop-blur-md rounded-t-2xl px-5 py-4 flex justify-between items-center border-b border-gray-100 z-10 shrink-0">
                        <div>
                            <h3 className="font-bold text-secondary text-lg">{selectedGroup.title}</h3>
                            <p className="text-sm text-gray-500">{selectedGroup.items.length > 0 ? `${selectedGroup.items.length} Properties found here` : 'Project Location'}</p>
                        </div>
                        <button onClick={() => setSelectedGroup(null)} className="p-2 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors self-start">
                            <X className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto p-4 custom-scrollbar flex-1">
                        {selectedGroup.items.length > 0 ? (
                            <div className="space-y-4">
                                {selectedGroup.items.map((unit: any) => (
                                    <Link to={`/units/${unit.slug || unit.id}`} key={unit.id} className="flex gap-4 p-2 rounded-xl hover:bg-gray-50 transition-colors group/card border border-transparent hover:border-gray-100">
                                        <div className="relative shrink-0">
                                            <RemoteImage src={unit.image} alt={unit.title} className="w-24 h-24 object-cover rounded-xl shadow-sm" />
                                            <div className="absolute top-1 right-1 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] font-bold text-secondary shadow-sm">
                                                {unit.type}
                                            </div>
                                        </div>
                                        <div className="flex flex-col justify-center flex-1">
                                            <h4 className="font-bold text-sm text-secondary line-clamp-2 group-hover/card:text-primary transition-colors leading-tight">{unit.title}</h4>
                                            <div className="flex shrink-0 gap-2 text-xs text-gray-500 mt-1.5 font-medium">
                                                <span>{unit.specs?.beds || unit.beds} Beds</span>
                                                <span>•</span>
                                                <span>{unit.specs?.area || unit.area} m²</span>
                                            </div>
                                            <p className="text-[15px] font-bold text-primary mt-2">{unit.price} EGP</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center flex flex-col items-center">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                    <Map className="w-8 h-8 text-primary" />
                                </div>
                                <h4 className="font-bold text-secondary text-lg mb-2">{selectedGroup.title}</h4>
                                <p className="text-sm text-gray-500 mb-6 max-w-[250px]">{t('search.map.viewProject', 'View full project details to explore available units and amenities.')}</p>
                                <Link to={`/projects/${selectedGroup.title.toLowerCase().replace(/\s+/g, '-')}`} className="bg-primary hover:bg-primary-dark transition-colors text-white font-bold px-8 py-3 rounded-xl shadow-lg shadow-primary/30 w-full">
                                    {t('search.map.explore', 'Explore Project')}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Custom Styles for Overrides */}
            <style>{`
                .leaflet-popup-content-wrapper {
                    border-radius: 12px;
                    padding: 4px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
                }
                .leaflet-popup-content {
                    margin: 8px 12px;
                }
                .custom-popup .leaflet-popup-tip {
                    box-shadow: none;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e5e7eb;
                    border-radius: 4px;
                }
                .custom-scrollbar:hover::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                }
                @keyframes slide-up {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up {
                    animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </div>
    );
};
